import { ISO3166Alpha2OrUnknown } from '@bits/i18n';
import { RPCSchemas } from '@bits/rpc';
import { AdvancedFilterGroup } from '@bits/ui';
import {
  Archive,
  CalendarCheck,
  CalendarPlus,
  Circle,
  Cube,
  FileText,
  FlowArrow,
  Globe,
  GlobeHemisphereWest,
  Nut,
  Question,
  UserFocus,
  WarningDiamond,
} from '@phosphor-icons/react';
import { TFunction } from 'i18next';

import {
  getCustomPropertyTranslationKey,
  getCustomTypeTranslationKey,
} from '@/lib/custom-properties/custom-properties-translations';
import { ScreeningStatus } from '@/lib/validations/bits-api/entities/get-entity-response';
import { ScreeningStatusIndicator } from '@/components/entities/screening-status-indicator';
import { MarketFlag } from '@/components/market-flag/market-flag';

export const facetAdvancedFilterOptionTransformMap: {
  [T in RPCSchemas['SearchFacet']['type']]: (
    facet: RPCSchemas['SearchFacet'],
    t: TFunction,
    filterValueTransforms?: Record<string, (value: string) => React.ReactNode>,
    facetOptionTransforms?: FilterFacetOptionTransform
  ) => AdvancedFilterGroup | undefined;
} = {
  VALUE: (facet, t, filterValueTransforms, facetOptionTransforms) => {
    if (facet.type !== 'VALUE') {
      return undefined;
    }

    const facetOption = getFacetOption(facet, facetOptionTransforms);

    const options = facet.values?.map((v) => ({
      label:
        filterValueTransforms?.[facet.name]?.(v.value) ||
        facetOption?.transformOptionLabel?.(facet, v.value, t) ||
        v.value,
      value: v.value,
      count: v.count,
    }));

    if (!options) return undefined;

    return {
      id: facet.name,
      label:
        typeof facetOption.label === 'function'
          ? facetOption.label(facet, t)
          : facetOption.label,
      icon: facetOption.icon,
      type: 'options',
      options,
    };
  },
  DATE_RANGE: (facet, t, filterValueTransforms, facetOptionTransforms) => {
    if (facet.type !== 'DATE_RANGE') {
      return undefined;
    }

    const facetOption = getFacetOption(facet, facetOptionTransforms);

    const presets = facet.values?.map((v) => ({
      label: String(
        filterValueTransforms?.[facet.name]?.(v.name) ||
          facetOption.transformOptionLabel?.(facet, v.name, t) ||
          v.name
      ),
      from: v.from,
      to: v.to,
      count: v.count,
    }));

    if (!presets) return undefined;

    return {
      id: facet.name,
      label:
        typeof facetOption.label === 'function'
          ? facetOption.label(facet, t)
          : facetOption.label,
      icon: facetOption.icon,
      type: 'date-range',
      presets,
    };
  },
  BOOLEAN: (facet, t, filterValueTransforms, facetOptionTransforms) => {
    if (facet.type !== 'BOOLEAN') {
      return undefined;
    }

    const facetOption = getFacetOption(facet, facetOptionTransforms);

    return {
      id: facet.name,
      label:
        typeof facetOption.label === 'function'
          ? facetOption.label(facet, t)
          : facetOption.label,
      icon: facetOption.icon,
      type: 'options',
      options: [
        {
          label: 'Yes',
          value: 'true',
        },
        {
          label: 'No',
          value: 'false',
        },
      ],
    } satisfies AdvancedFilterGroup;
  },
};

const getFacetOption = (
  facet: RPCSchemas['SearchFacet'],
  facetTransforms?: FilterFacetOptionTransform
) => {
  // If this is a custom property
  if (facet.origin === 'CUSTOM') {
    return filterNameToComponentMap._customProperty as NonNullable<
      (typeof filterNameToComponentMap)[string]
    >;
  }

  return (
    facetTransforms?.[facet.name] ||
    filterNameToComponentMap[facet.name] ||
    (filterNameToComponentMap._default as NonNullable<
      (typeof filterNameToComponentMap)[string]
    >)
  );
};

const filterNameToComponentMap: {
  [key: string]: {
    label:
      | ((facet: RPCSchemas['SearchFacet'], t: TFunction) => string)
      | string;
    icon: React.ElementType;
    transformOptionLabel?: (
      facet: RPCSchemas['SearchFacet'],
      value: string,
      t: TFunction
    ) => React.ReactNode;
  };
} = {
  _default: {
    label: (facet) => facet.name,
    icon: Question,
  },

  _customProperty: {
    label: (facet, t) => {
      return t(getCustomPropertyTranslationKey(facet.name));
    },
    icon: Cube,
    transformOptionLabel: (facet, value, t) =>
      'valueName' in facet && facet.valueName
        ? t(getCustomTypeTranslationKey(facet.valueName, value), value)
        : value,
  },

  archived: {
    label: 'Archived',
    icon: Archive,
  },
  createdAt: {
    label: 'Created date',
    icon: CalendarPlus,
  },
  updatedAt: {
    label: 'Last updated date',
    icon: CalendarCheck,
  },
  publishedAt: {
    label: 'Published date',
    icon: CalendarCheck,
  },
  status: {
    label: 'Status',
    icon: Circle,
  },
  itemType: {
    label: 'Item type',
    icon: WarningDiamond,
    transformOptionLabel: (facet, value, t) => t(`enums:${value}`, value),
  },
  itemTypes: {
    label: 'Item type',
    icon: WarningDiamond,
    transformOptionLabel: (facet, value, t) => t(`enums:${value}`, value),
  },
  assignedTo: {
    label: 'Assignee',
    icon: UserFocus,
  },
  entityType: {
    label: 'Entity type',
    icon: Nut,
    transformOptionLabel: (facet, value, t) => {
      if (value === '') {
        return t('enums:UNKNOWN');
      }
      return t(`enums:${value}`, value);
    },
  },
  market: {
    label: 'Market',
    icon: Globe,
    transformOptionLabel: (facet, value) => (
      <MarketFlag countryAlpha2={value as ISO3166Alpha2OrUnknown} />
    ),
  },
  markets: {
    label: 'Market',
    icon: Globe,
    transformOptionLabel: (facet, value) => (
      <MarketFlag countryAlpha2={value as ISO3166Alpha2OrUnknown} />
    ),
  },
  workflowName: {
    label: 'Workflow',
    icon: FlowArrow,
    transformOptionLabel: (facet, value, t) => t(`enums:${value}`, value),
  },
  registeredCountry: {
    label: 'Registered country',
    icon: GlobeHemisphereWest,
    transformOptionLabel: (facet, value) => (
      <MarketFlag countryAlpha2={value as ISO3166Alpha2OrUnknown} />
    ),
  },
  associatedCountries: {
    label: 'Associated countries',
    icon: GlobeHemisphereWest,
    transformOptionLabel: (facet, value) => (
      <MarketFlag countryAlpha2={value as ISO3166Alpha2OrUnknown} />
    ),
  },
  associatedMarkets: {
    label: 'Associated markets',
    icon: GlobeHemisphereWest,
    transformOptionLabel: (facet, value) => (
      <MarketFlag countryAlpha2={value as ISO3166Alpha2OrUnknown} />
    ),
  },
  applicationRelations: {
    label: 'Association',
    icon: FileText,
    transformOptionLabel: (facet, value, t) => t(`enums:${value}`, value),
  },
  pepStatus: {
    label: 'PEP status',
    icon: UserFocus,
    transformOptionLabel: (facet, value, t) => (
      <div className="flex items-center gap-2">
        <ScreeningStatusIndicator
          showText={false}
          status={value as ScreeningStatus}
        />
        {t(`enums:${value}`)}
      </div>
    ),
  },
  sanctionStatus: {
    label: 'Sanctions status',
    icon: UserFocus,
    transformOptionLabel: (facet, value, t) => (
      <div className="flex items-center gap-2">
        <ScreeningStatusIndicator
          showText={false}
          status={value as ScreeningStatus}
        />
        {t(`enums:${value}`)}
      </div>
    ),
  },
};

export type FilterFacetOptionTransform = typeof filterNameToComponentMap;
